import { PublicClientApplication, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { config } from '../config.js';

// One app registration serves the SPA and exposes the API, so the API scope
// lives under the same client id.
export const apiScopes = [`api://${config.clientId}/access_as_user`];

// Redirect lands on /auth on whatever origin we run from:
//   web      https://nano.nanoutilities.co.uk/auth
//   android  https://localhost/auth (Capacitor serves the bundled app there)
//   dev      http://localhost:5173/auth
// All three are registered as SPA redirect URIs on the app registration.
export const msal = new PublicClientApplication({
  auth: {
    clientId: config.clientId,
    authority: `https://login.microsoftonline.com/${config.tenantId}`,
    redirectUri: `${window.location.origin}/auth`,
    postLogoutRedirectUri: `${window.location.origin}/`,
    navigateToLoginRequestUrl: true,
  },
  // localStorage so the app can start signed in with no signal.
  cache: { cacheLocation: 'localStorage' },
});

export class OfflineError extends Error {
  constructor() {
    super('No connection');
    this.code = 'offline';
  }
}

export function signIn() {
  return msal.loginRedirect({ scopes: apiScopes, prompt: 'select_account' });
}

export async function signOut() {
  // Imported here to avoid a cycle (push uses the API client, which uses this).
  const { disablePush } = await import('../native/push.js');
  await disablePush();
  try { localStorage.removeItem('nu.inbox'); localStorage.removeItem('nu.inbox.marks'); } catch { /* fine */ }
  return msal.logoutRedirect({ account: msal.getActiveAccount() });
}

// Access token for the API. Never starts an interactive sign-in by itself
// while offline: the caller keeps the work queued and tries again later.
export async function getApiToken() {
  const account = msal.getActiveAccount() || msal.getAllAccounts()[0];
  if (!account) throw new InteractionRequiredAuthError('no_account');
  try {
    const res = await msal.acquireTokenSilent({ scopes: apiScopes, account });
    return res.accessToken;
  } catch (e) {
    if (!navigator.onLine) throw new OfflineError();
    // A plain network failure: no signal.
    if (e instanceof BrowserAuthError && /network|post_request_failed/i.test(e.errorCode || '')) throw new OfflineError();
    // Anything else (a timeout included) is usually the sign-in itself running
    // out: Microsoft only lets a browser renew silently for about a day, and
    // the fallback hidden frame is blocked by browsers. Signal or no signal?
    if (e instanceof BrowserAuthError && !(await serverReachable())) throw new OfflineError();
    return signInAgain(account, e);
  }
}

async function serverReachable() {
  try {
    const res = await fetch(`${config.apiBase}/api/health`, { cache: 'no-store', signal: AbortSignal.timeout(5000) });
    return res.ok;
  } catch {
    return false;
  }
}

// Round trip through Microsoft sign-in (normally no typing: they're still
// signed in to Microsoft) and back to the same page. Only while someone is
// looking, and at most once a minute so a real fault can't loop.
const LAST_REDIRECT = 'nu.signInAgainAt';
async function signInAgain(account, cause) {
  let last = 0;
  try { last = Number(sessionStorage.getItem(LAST_REDIRECT)) || 0; } catch { /* storage blocked */ }
  if (document.visibilityState !== 'visible' || Date.now() - last < 60_000) throw cause;
  try { sessionStorage.setItem(LAST_REDIRECT, String(Date.now())); } catch { /* storage blocked */ }
  await msal.acquireTokenRedirect({ scopes: apiScopes, account, loginHint: account.username });
  return new Promise(() => {}); // the page is leaving
}
